import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon, ChevronRightIcon,
  AcademicCapIcon, ClockIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sr-root * { box-sizing:border-box; }
  .sr-root { font-family:'Sora',sans-serif; color:#1a1d2e; animation:srFade .4s ease both; }
  @keyframes srFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  .sr-header { margin-bottom:1.75rem; }
  .sr-title  { font-size:1.6rem; font-weight:700; letter-spacing:-.03em; color:#0f1123; }
  .sr-sub    { font-size:.78rem; color:#9094a8; margin-top:3px; }

  .sr-search-wrap { position:relative; margin-bottom:1.5rem; }
  .sr-search-wrap svg { position:absolute; left:14px; top:50%; transform:translateY(-50%); width:16px; height:16px; color:#9094a8; stroke-width:2; pointer-events:none; }
  .sr-search { font-family:'Sora',sans-serif; font-size:.84rem; width:100%; max-width:360px; padding:11px 14px 11px 40px; border:1.5px solid #e4e7f0; border-radius:12px; background:white; color:#1a1d2e; outline:none; transition:border-color .2s,box-shadow .2s; }
  .sr-search:focus { border-color:#3b6ef8; box-shadow:0 0 0 3px rgba(59,110,248,.09); }
  .sr-search::placeholder { color:#c0c5dc; }

  .teacher-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1rem; }
  .teacher-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.3rem 1.4rem; cursor:pointer; position:relative; overflow:hidden; transition:transform .18s,box-shadow .18s,border-color .18s; animation:srFade .4s ease both; }
  .teacher-card:hover { transform:translateY(-2px); box-shadow:0 8px 28px rgba(59,110,248,.1); border-color:#c8d5fd; }
  .teacher-card::after { content:''; position:absolute; top:0; right:0; width:80px; height:80px; border-radius:0 16px 0 80px; background:#3b6ef8; opacity:.04; }

  .tc-top    { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1rem; }
  .tc-avatar { width:44px; height:44px; border-radius:12px; background:#ecfdf5; color:#10c98e; display:flex; align-items:center; justify-content:center; font-size:.9rem; font-weight:700; text-transform:uppercase; flex-shrink:0; }
  .tc-arrow  { width:28px; height:28px; border-radius:8px; background:#f0f2f7; color:#9094a8; display:flex; align-items:center; justify-content:center; transition:background .15s,color .15s; }
  .tc-arrow svg { width:14px; height:14px; stroke-width:2.5; }
  .teacher-card:hover .tc-arrow { background:#eef2ff; color:#3b6ef8; }
  .tc-name  { font-size:.9rem; font-weight:700; color:#0f1123; margin-bottom:2px; }
  .tc-email { font-size:.72rem; color:#9094a8; margin-bottom:10px; }
  .tc-chips { display:flex; gap:6px; flex-wrap:wrap; }
  .tc-chip  { display:inline-flex; align-items:center; gap:4px; font-size:.68rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .tc-chip svg { width:11px; height:11px; stroke-width:2; }
  .tc-chip.emp  { background:#fffbeb; color:#92400e; }
  .tc-chip.rate { background:#ecfdf5; color:#065f46; font-family:'JetBrains Mono',monospace; }

  .sr-empty { text-align:center; padding:5rem 2rem; }
  .sr-empty-icon { width:56px; height:56px; border-radius:14px; background:#eef2ff; color:#3b6ef8; display:flex; align-items:center; justify-content:center; margin:0 auto 14px; }
  .sr-empty-icon svg { width:26px; height:26px; stroke-width:1.6; }
  .sr-empty-title { font-size:.95rem; font-weight:600; color:#1a1d2e; margin-bottom:5px; }
  .sr-empty-sub   { font-size:.78rem; color:#9094a8; }

  .sr-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; font-size:.88rem; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  /* ── Mobile ── */
  @media (max-width:768px) {
    .sr-title { font-size:1.2rem; }
    .sr-header { margin-bottom:1.2rem; }
    /* Search goes full width on mobile */
    .sr-search { max-width:100%; }
    /* Cards already auto-fill — no change needed */
  }
`;

const initials = (name='') =>
  name.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';

const SalaryRecords = () => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');

  useEffect(() => {
    api.get('/admin/salary/teachers')
      .then(({data}) => setTeachers(data))
      .catch(() => toast.error('Failed to load teachers'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = teachers.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.name?.toLowerCase().includes(q) || t.employeeId?.toLowerCase().includes(q);
  });

  return (
    <div className="sr-root">
      <style>{style}</style>
      <div className="sr-header">
        <h1 className="sr-title">Salary Records</h1>
        <p className="sr-sub">Select a teacher to view and manage their monthly salary.</p>
      </div>
      <div className="sr-search-wrap">
        <MagnifyingGlassIcon />
        <input className="sr-search" placeholder="Search by name or employee ID…"
          value={search} onChange={e=>setSearch(e.target.value)} />
      </div>
      {loading ? (
        <div className="sr-loading"><div className="spinner"/>Loading teachers…</div>
      ) : filtered.length===0 ? (
        <div className="sr-empty">
          <div className="sr-empty-icon"><AcademicCapIcon /></div>
          <div className="sr-empty-title">{search?'No teachers found':'No teachers yet'}</div>
          <div className="sr-empty-sub">{search?'Try a different search.':'Add teachers from Settings → Create Account.'}</div>
        </div>
      ) : (
        <div className="teacher-grid">
          {filtered.map((t,i) => (
            <div key={t.teacherId} className="teacher-card"
              style={{animationDelay:`${i*.04}s`}}
              onClick={()=>navigate(`/salary-records/${t.teacherId}`)}>
              <div className="tc-top">
                <div className="tc-avatar">{initials(t.name)}</div>
                <div className="tc-arrow"><ChevronRightIcon /></div>
              </div>
              <div className="tc-name">{t.name}</div>
              <div className="tc-email">{t.email}</div>
              <div className="tc-chips">
                {t.employeeId&&<span className="tc-chip emp"><AcademicCapIcon />{t.employeeId}</span>}
                {t.salaryPerHour>0&&<span className="tc-chip rate"><ClockIcon />Rs.{t.salaryPerHour}/hr</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SalaryRecords;